/**
 * The ALIGNED path — the preferred first pass, and the composer that runs it.
 *
 * Gemini (or WhisperX) is asked only WHAT was said; WHERE comes from a physical
 * measurement — the local MMS forced aligner, or WhisperX's own alignment — and
 * the VAD labels whatever is left, so the output is a PARTITION of the whole
 * recording rather than a set of guessed windows.
 *
 * WHAT THIS FILE OWNS
 * -------------------
 * The run: build the tiles, mint the task code, slice, precompute the Verify
 * picker's alternatives, persist, close the log. It was lifted out of
 * `firstPass.ts` verbatim (one uniform 4-space dedent, `options.x` -> `ctx.x`,
 * and the task-code minting routed through `db.ensureTaskCode`).
 *
 * THE FALL-THROUGH IS THE LOAD-BEARING PART
 * -----------------------------------------
 * {@link runAlignedPipeline} returns `null` to mean "I did not run; use the
 * legacy pipeline". Three rules decide, and they are the whole contract:
 *
 *   1. an ABORT rethrows before anything else — a cancelled run is not a
 *      failed engine;
 *   2. an EXPLICITLY chosen engine rethrows rather than being swapped out. A
 *      silent "WhisperX -> legacy" swap once produced six times the clips with
 *      no warning at all;
 *   3. anything else degrades to legacy, because the default engine has to work
 *      on a phone, which cannot run either helper.
 *
 * Do not restructure the try/catch below without re-reading those three.
 */

import { ensureTaskCode, getTask, newId, replaceSegmentsForTask, updateTask } from '../../db';
import { sliceSegmentsToWav } from '../audioSlicer';
import { describePlan, type EnginePlan } from '../enginePlan';
import { endRun, note } from '../pipelineLog';
import { PHASE_START, report, throwIfAborted, type ProgressCallback } from '../progress';
import { tilesToSegments, type AlignedTilesResult, type BuildTilesArgs } from './alignedTiling';
import { baseGemini } from './baseGemini';
import { baseWhisperx } from './baseWhisperx';
import { readCuts } from './stageReadCuts';
import { recutLongCuts } from './stageRecut';

/* ------------------------------------------------------------------ */
/* The composer                                                        */
/* ------------------------------------------------------------------ */

/**
 * The three filler booleans, derived ONCE.
 *
 * `plan.fillers` is a four-valued mode, and three stages each used to re-derive
 * its own boolean from it at three different depths of the old function — one
 * at the top of the WhisperX branch, two more eighty lines down inside the
 * cut-read block. Nothing was wrong with the values; what was wrong is that
 * "which modes make this stage behave differently" could only be answered by
 * reading the whole function. One shape, computed at the top, read everywhere.
 */
function fillerModes(mode: EnginePlan['fillers']): {
  /** Ask WhisperX itself for hesitations (both the first pass and any re-cut). */
  bias: boolean;
  /** Filler tiles are ground truth; Gemini may not relabel them. */
  force: boolean;
  /** Ask Gemini to pay attention to hesitations while reading a cut. */
  emphasize: boolean;
} {
  return {
    bias: mode === 'bias' || mode === 'force',
    force: mode === 'force',
    emphasize: mode === 'prompt',
  };
}

/**
 * Build the labeled timeline: pick a base, then run the optional stages the
 * plan asks for. Every stage after the base only ever REWRITES tiles — the
 * partition is established by `alignedTiling.tileFromWords` and preserved by
 * each stage's own seam handling.
 *
 * `args.plan` is already normalised (`firstPass` does it once, at the entry to
 * the run); nothing in here re-normalises or re-spells the default.
 */
export async function buildAlignedTiles(args: BuildTilesArgs): Promise<AlignedTilesResult> {
  const { buffer, plan, signal, onPhase } = args;
  const fillers = fillerModes(plan.fillers);

  if (plan.base !== 'whisperx') return baseGemini(args);

  /* -- WHISPERX PATH: one model transcribes AND aligns --------------- */
  const tiled = await baseWhisperx(args, { biasFillers: fillers.bias });

  // WhisperX cut it; now Gemini reads each cut.
  if (plan.geminiCuts) {
    await readCuts({
      buffer,
      tiles: tiled.tiles,
      forceFillers: fillers.force,
      emphasizeFillers: fillers.emphasize,
      signal,
      onPhase,
    });

    /* -- re-segmentation: long clips go back to WhisperX ------------- */
    if (plan.recut !== 'none') {
      await recutLongCuts({
        buffer,
        tiled,
        guided: plan.recut === 'guided',
        biasFillers: fillers.bias,
        signal,
        onPhase,
      });
    }
  }
  return tiled;
}

/* ------------------------------------------------------------------ */
/* The run                                                             */
/* ------------------------------------------------------------------ */

/** Everything the run already established, handed over explicitly. */
export interface AlignedPipelineContext {
  taskId: string;
  /** The decoded original — the SAME buffer the upload was encoded from. */
  buffer: AudioBuffer;
  /** 16 kHz mono WAV of the whole recording. */
  uploadBlob: Blob;
  /** Already normalised by the caller; normalising twice is how drift starts. */
  plan: EnginePlan;
  /** `planToId(plan)` — stored on the task so a card can name its pipeline. */
  engine: string;
  /** The user CHOSE this engine, so it may not be silently swapped out. */
  explicit: boolean;
  signal?: AbortSignal;
  onProgress?: ProgressCallback;
  /** Seconds of padding added to each side of a clip. */
  padSec?: number;
}

export interface AlignedPipelineResult {
  taskId: string;
  segmentCount: number;
}

/**
 * Run the aligned pipeline to completion, or decline.
 *
 * Resolves to the result when it ran, or to `null` when it failed in a way the
 * caller is allowed to recover from by running the legacy pipeline instead.
 * Throws when it is not allowed to (abort, or an explicitly chosen engine).
 */
export async function runAlignedPipeline(
  ctx: AlignedPipelineContext,
): Promise<AlignedPipelineResult | null> {
  const { taskId, buffer, uploadBlob, plan, engine, explicit, signal, onProgress } = ctx;

  try {
    note(`engine: ${engine} — ${describePlan(plan)}`);
    const { tiles, language, unplacedNoise } = await buildAlignedTiles({
      buffer,
      uploadWav: uploadBlob,
      plan,
      signal,
      onPhase: (message, frac) =>
        report(onProgress, {
          phase: 'transcribing',
          message,
          ratio: PHASE_START.transcribing + frac * (PHASE_START.slicing - PHASE_START.transcribing),
        }),
    });
    throwIfAborted(signal);
    note(
      `aligned pipeline: ${tiles.length} tiles (language ${language}` +
        (unplacedNoise ? `, ${unplacedNoise} noise events found acoustically` : '') +
        ')',
    );

    // Version names need the recording's code (A1, A2, …): mint it now if
    // this task predates the naming rule. `db.ensureTaskCode` is the one
    // implementation — `recut.nextNames` had a byte-identical copy of it.
    const taskCode = await ensureTaskCode(taskId);
    const alignedSegments = tilesToSegments(tiles, taskId, newId, taskCode);
    report(onProgress, {
      phase: 'slicing',
      message: `Cutting ${alignedSegments.length} clips…`,
      ratio: PHASE_START.slicing,
      done: 0,
      total: alignedSegments.length,
    });
    const alignedClips = await sliceSegmentsToWav(buffer, alignedSegments, {
      padSec: ctx.padSec,
      onProgress: (done, total) => {
        report(onProgress, {
          phase: 'slicing',
          message: `Cutting clips… ${done}/${total}`,
          ratio:
            PHASE_START.slicing +
            (total ? (done / total) * (PHASE_START.saving - PHASE_START.slicing) : 0),
          done,
          total,
        });
      },
    });
    throwIfAborted(signal);

    /* -- correction options, precomputed ---------------------------- */
    /*
     * The Verify screen's word picker must open instantly, so the guesses
     * are produced now rather than mid-review. Best effort: a failure here
     * only costs the picker its suggestions.
     */
    try {
      const { attachAlternatives } = await import('../alternatives');
      const alt = await attachAlternatives(buffer, alignedSegments, {
        signal,
        onProgress: (d, t) =>
          report(onProgress, {
            phase: 'qa',
            message: `Preparing correction options… ${d}/${t}`,
            ratio: PHASE_START.qa + (t ? (d / t) * 0.25 : 0),
            done: d,
            total: t,
          }),
      });
      note(`alternatives: ${alt.withAlternatives} clips across ${alt.sentences} sentences`);
    } catch (err) {
      note(`alternatives stage skipped: ${err instanceof Error ? err.message : String(err)}`);
    }
    throwIfAborted(signal);

    report(onProgress, { phase: 'saving', message: 'Saving clips…', ratio: PHASE_START.saving });
    if (!(await getTask(taskId))) throw new Error('That task was deleted while it was processing.');
    await replaceSegmentsForTask(
      taskId,
      alignedSegments.map((s, i) => ({ segment: s, clip: alignedClips[i] })),
    );

    await updateTask(taskId, { status: 'review', error: undefined, language, engine });
    await endRun('ok');
    const flagged = alignedSegments.filter((s) => s.confidence <= 0.3).length;
    report(onProgress, {
      phase: 'done',
      message:
        flagged > 0
          ? `${alignedSegments.length} tiles ready to verify · ${flagged} flagged by the aligner`
          : `${alignedSegments.length} tiles ready to verify`,
      ratio: 1,
      done: alignedSegments.length,
      total: alignedSegments.length,
    });
    return { taskId, segmentCount: alignedSegments.length };
  } catch (err) {
    if (signal?.aborted) throw err;
    // The helper being flaky must never break ingestion — fall back to the
    // legacy pipeline, which needs nothing but Gemini.
    console.warn('[lableforge] aligned pipeline failed, falling back to legacy:', err);
    note(`aligned pipeline threw: ${err instanceof Error ? err.message : String(err)}`);
    // Same rule: a chosen engine must never be swapped out behind the user.
    if (explicit) throw err;
  }

  return null;
}
